import { useEffect, useRef } from 'react'

import kakaopayLogo from '@/assets/payment/kakaopay.png'
import type { PaymentMethodPgType } from '@/payment/types'

import { AddCardSlide } from './AddCardSlide'
import { extractCardIssuer } from './cardAlias'
import {
  AddButton,
  CardAlias,
  CardAreaHint,
  CardIssuer,
  CarouselContainer,
  EmptyCardButton,
  EmptyCardContainer,
  EmptyCardHint,
  KakaopayBadge,
  Pagination,
  PaginationCurrent,
  PaginationMuted,
  PlusIcon,
  RegisteredCardSlide,
  SlideLabel,
  SlidesTrack,
} from './styles'

/**
 * 캐러셀에 표시할 등록 카드.
 *
 * `GET /payments/methods` 응답의 `data.content[]`을 normalize한 형태.
 * `alias`("[비씨] 2089")는 백엔드가 만들어주므로 그대로 표시.
 * `pgType` 으로 카드 디자인 분기 — KAKAOPAY 는 노란 배경 + 카카오페이 로고.
 */
export interface RegisteredCard {
  id: string
  alias: string
  pgType?: PaymentMethodPgType
}

interface CardCarouselProps {
  /** GET /payments/methods 결과 — 정규화된 카드 목록 */
  cards: RegisteredCard[]
  /** AddCardSlide 클릭 시 호출 — 카드 등록 진입점 */
  onAddCard: () => void
  /** 현재 선택된 카드 id — 페이지네이션 current 표시 + 결제 대상. 미지정 시 첫 카드. */
  selectedCardId?: string | null
  /**
   * 스와이프로 화면에 들어온 슬라이드가 바뀌면 호출.
   * 카드면 그 카드 id, "결제 카드 추가" 슬라이드면 null (부모가 결제 버튼 disable 판단).
   */
  onSelectCard?: (id: string | null) => void
}

/** AddCardSlide 식별용 sentinel — add 슬라이드 활성 여부 감지. 실제 카드 id 와 충돌 없게. */
const ADD_SLIDE_ID = '__add_card_slide__'

/** styles.ts SlidesTrack 의 scroll-padding-left/padding-left(32px) 와 일치 — snap 시 카드 좌측이 놓이는 기준선 */
const SNAP_OFFSET_PX = 32
/** 스와이프가 멈춘 뒤 선택 확정까지 debounce (ms) */
const SCROLL_SETTLE_MS = 120

/**
 * 카드 결제 분기 캐러셀.
 *
 * 슬라이드 구성:
 *  - 등록 카드 0개 → AddCardSlide 1개만 (empty state, "카드를 등록해 주세요")
 *  - 등록 카드 N개 → 등록 카드 N개 + AddCardSlide ("결제 카드 추가")
 *
 * 스와이프 ↔ 선택 동기화: IntersectionObserver 로 화면에 가장 많이 보이는 카드를 감지해
 * onSelectCard 로 부모에 알린다. 페이지네이션 current 와 결제 대상 카드가 스와이프를 따라간다.
 */
export function CardCarousel({ cards, onAddCard, selectedCardId, onSelectCard }: CardCarouselProps) {
  const isEmpty = cards.length === 0
  const trackRef = useRef<HTMLDivElement>(null)
  const onSelectRef = useRef(onSelectCard)
  // 마지막으로 결제 대상으로 보고/스크롤한 카드 id — 사용자 스와이프 ↔ 프로그램 스크롤 루프 방지
  const lastSyncedRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    onSelectRef.current = onSelectCard
  }, [onSelectCard])

  const cardKey = cards.map((card) => card.id).join('|')

  // 스와이프가 멈추면 좌측 snap 기준선에 가장 가까운 슬라이드를 결제 대상으로 보고한다.
  // IntersectionObserver 는 WebView scroll-snap 에서 정지 콜백이 누락돼 선택이 첫 카드에 고정되는
  // 경우가 있어, 스크롤 위치 기반 결정론적 계산으로 대체.
  useEffect(() => {
    const track = trackRef.current
    if (!track) return undefined

    let timer: ReturnType<typeof setTimeout> | undefined

    const detectSelected = () => {
      const slides = Array.from(track.querySelectorAll<HTMLElement>('[data-card-id]'))
      if (slides.length === 0) return
      const snapLine = track.getBoundingClientRect().left + SNAP_OFFSET_PX
      let nearestId: string | null = null
      let nearestDist = Infinity
      for (const slide of slides) {
        const dist = Math.abs(slide.getBoundingClientRect().left - snapLine)
        if (dist < nearestDist) {
          nearestDist = dist
          nearestId = slide.getAttribute('data-card-id')
        }
      }
      const id = nearestId === ADD_SLIDE_ID ? null : nearestId
      if (id === lastSyncedRef.current) return
      lastSyncedRef.current = id
      onSelectRef.current?.(id)
    }

    const handleScroll = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(detectSelected, SCROLL_SETTLE_MS)
    }

    track.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      track.removeEventListener('scroll', handleScroll)
      if (timer) clearTimeout(timer)
    }
  }, [cardKey])

  // 외부에서 selectedCardId 가 바뀌었는데(등록 직후 자동선택 등) 현재 스크롤 위치와 다르면 해당 카드로 스크롤.
  // 사용자 스와이프로 인한 변경은 이미 lastSyncedRef 와 같아 스킵 → 루프 방지.
  useEffect(() => {
    if (selectedCardId == null || selectedCardId === lastSyncedRef.current) return
    const track = trackRef.current
    if (!track) return
    const slides = Array.from(track.querySelectorAll<HTMLElement>('[data-card-id]'))
    const target = slides.find((slide) => slide.getAttribute('data-card-id') === selectedCardId)
    if (!target) return
    lastSyncedRef.current = selectedCardId
    const delta = target.getBoundingClientRect().left - (track.getBoundingClientRect().left + SNAP_OFFSET_PX)
    // scrollBy 미지원 환경(jsdom 등) 방어
    track.scrollBy?.({ left: delta, behavior: 'smooth' })
  }, [selectedCardId, cardKey])

  const selectedIndex = cards.findIndex((card) => card.id === selectedCardId)
  const currentIndex = selectedIndex >= 0 ? selectedIndex : 0

  if (isEmpty) {
    return (
      <EmptyCardContainer>
        <EmptyCardButton
          type='button'
          onClick={onAddCard}
        >
          <AddButton>
            <PlusIcon />
          </AddButton>
          <SlideLabel>결제 카드 추가</SlideLabel>
        </EmptyCardButton>
        <EmptyCardHint>본인 명의의 카드만 등록 가능합니다</EmptyCardHint>
      </EmptyCardContainer>
    )
  }

  return (
    <CarouselContainer>
      <SlidesTrack ref={trackRef}>
        {cards.map((card) => {
          const isKakaopay = card.pgType === 'KAKAOPAY'
          return (
            <RegisteredCardSlide
              key={card.id}
              data-card-id={card.id}
              isKakaopay={isKakaopay}
            >
              {isKakaopay && (
                <KakaopayBadge
                  data-testid='card-kakaopay-badge'
                  src={kakaopayLogo}
                  alt=''
                />
              )}
              <CardIssuer isKakaopay={isKakaopay}>{extractCardIssuer(card.alias)}</CardIssuer>
              <CardAlias isKakaopay={isKakaopay}>{card.alias}</CardAlias>
            </RegisteredCardSlide>
          )
        })}
        <AddCardSlide
          slideId={ADD_SLIDE_ID}
          label='결제 카드 추가'
          onClick={onAddCard}
        />
      </SlidesTrack>
      {/* AddCardSlide 활성(selectedCardId===null)이면 힌트, 그 외엔 페이지네이션 */}
      {selectedCardId === null ? (
        <CardAreaHint>본인 명의의 카드만 등록 가능합니다</CardAreaHint>
      ) : (
        <Pagination>
          <PaginationCurrent data-testid='card-carousel-current'>{currentIndex + 1}</PaginationCurrent>
          <PaginationMuted>/</PaginationMuted>
          <PaginationMuted data-testid='card-carousel-total'>{cards.length}</PaginationMuted>
        </Pagination>
      )}
    </CarouselContainer>
  )
}
